import type { PublicStatusState } from '@nadoutong/contracts';
import type { useT } from '../i18n/index.ts';
import type { ChipTone } from '../ui/Chip.tsx';
import type { LampTone } from '../ui/StatusLamp.tsx';

/**
 * The shared `/v1/status` vocabulary: how a `PublicStatusState` looks
 * and how it reads.
 *
 * One map, not two: the lamp and the chip must never disagree about
 * what a state means, and `/dashboard` must never paint a state a
 * different color than `/status` does for the same payload. Two
 * hand-kept tables drift silently — both pages assert their own tone
 * literals, so nothing would go red. `LampTone & ChipTone` is the
 * shared part of both vocabularies.
 */
export const STATE_TONE: Record<PublicStatusState, LampTone & ChipTone> = {
  healthy: 'ok',
  busy: 'warn',
  unavailable: 'bad',
};

/**
 * The one-word rendering of a state. Colour alone never carries it
 * (WCAG 1.4.1): every surface that paints a lamp or a chip also spells
 * the state out, visibly or for assistive tech only.
 */
export function stateText(state: PublicStatusState, t: ReturnType<typeof useT>): string {
  switch (state) {
    case 'healthy':
      return t('status.state.healthy');
    case 'busy':
      return t('status.state.busy');
    case 'unavailable':
      return t('status.state.unavailable');
  }
}
